import classNames from "classnames";
import upperFirst from "lodash/upperFirst";
import React, { FunctionComponent, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import CartBreadcrumb from "@Components/atoms/cart/CartBreadcrumb";
import NotificationsArea from "@Components/molecules/cart/NotificationsArea";
import useCartContext from "@Hooks/useCartContext";
import useNotificationsContext from "@Hooks/useNotificationsContext";
import useRouterContext from "@Lib/router/hooks/useRouterContext";
import { ApiErrorResponse } from "@Types/api/error";
import { Step } from "@Types/cart";
import { convertFromApiToCartContent } from "@Utils/cart/cart";
import { convertFromPromoCodeContentToApi } from "@Utils/cart/promoCode";
import { getMetaContent } from "@Utils/meta";
import {
  convertToCartProductProperties,
  convertToIdentifyProperties,
  convertToOrderUpdatedProperties,
  setDataLayerTrack,
} from "@Utils/track";
import CartAddressStep from "./CartAddressStep";
import CartCheckoutStep from "./CartCheckoutStep";
import CartOverviewStep from "./CartOverviewStep";
import "./style.scss";

interface Props {
  onChangeCreditCard: () => void;
  onCompleteOrder: () => void;
  onSaveCreditCard: () => void;
  onUpdatePromoCode: (promoCode: string | undefined) => void;
  onValidatePostcode: (
    postcode: string | undefined,
    countryTag: string | undefined
  ) => Promise<boolean>;
  promoCodeErrorMessage: string | undefined;
  step: Step;
}

interface ContainerProps {
  onChangeCreditCard: () => void;
  onCompleteOrder: () => void;
  onEmptyCart: () => void;
  onSaveCreditCard: () => void;
  onValidatePostcode: (
    postcode: string | undefined,
    countryTag: string | undefined
  ) => Promise<boolean>;
  step: Step;
}

export const Cart: FunctionComponent<Props> = ({
  onChangeCreditCard,
  onCompleteOrder,
  onSaveCreditCard,
  onUpdatePromoCode,
  onValidatePostcode,
  promoCodeErrorMessage,
  step,
}) => {
  const { cart, configuration, dispatch, ui } = useCartContext();
  const routerContext = useRouterContext();

  useEffect(() => {
    if (cart.lockedDuringPayment) {
      dispatch({ type: "setCartStep", step: "overview" });
    }
  }, []);

  useEffect(() => {
    dispatch({ type: "setCartStep", step });
  }, [step, dispatch]);

  const handleChangeStep = (newStep: Step) => {
    dispatch({ type: "setCartStep", step: newStep });
    routerContext.history?.push(`/${configuration.localization}/cart#/${newStep}`);
  };

  return (
    <div
      className={classNames("Cart", {
        "Cart--disabled": ui.disabled,
        "Cart--withoutCategories": ui.step !== "checkout",
      })}
    >
      {cart.articles.length > 0 && (
        <div className="Cart-content-line">
          <CartBreadcrumb activeStep={ui.step} onClick={handleChangeStep} steps={ui.steps} />
        </div>
      )}

      <div className="Cart-notifications">
        <NotificationsArea />
      </div>

      {cart.articles.length > 0 && ui.step === "checkout" && (
        <CartCheckoutStep
          disabled={ui.disabled}
          onChangeStep={handleChangeStep}
          onUpdatePromoCode={onUpdatePromoCode}
          promoCodeErrorMessage={promoCodeErrorMessage}
        />
      )}
      {ui.step === "address" && (
        <CartAddressStep
          disabled={ui.disabled}
          onChangeStep={handleChangeStep}
          onValidatePostcode={onValidatePostcode}
        />
      )}
      {ui.step === "overview" && (
        <CartOverviewStep
          onChangeCreditCard={onChangeCreditCard}
          onCompleteOrder={onCompleteOrder}
          onSaveCreditCard={onSaveCreditCard}
          onChangeStep={handleChangeStep}
          onUpdatePromoCode={onUpdatePromoCode}
          promoCodeErrorMessage={promoCodeErrorMessage}
        />
      )}
    </div>
  );
};

const CartContainer: FunctionComponent<ContainerProps> = ({
  onChangeCreditCard,
  onCompleteOrder,
  onEmptyCart,
  onSaveCreditCard,
  onValidatePostcode,
  step,
}) => {
  const { cart, configuration, dispatch, user } = useCartContext();
  const intl = useIntl();
  const { dispatchNotification } = useNotificationsContext();
  const [promoCodeErrorMessage, setPromoCodeErrorMessage] = useState<string | undefined>();

  useEffect(() => {
    setDataLayerTrack("Global", {
      currency: cart.currency,
      environment: getMetaContent("cheerz-env") === "production" ? "production" : "test",
      locale: intl.locale,
    });
    setDataLayerTrack("Identify", convertToIdentifyProperties(user));
    setDataLayerTrack("Checkout Started", {
      cartId: cart.orderNumber,
      products: cart.articles.map(convertToCartProductProperties),
      revenue: cart.revenue,
      shipping: cart.shippingTotal.cents,
      tax: cart.tax,
      total: cart.total.cents,
    });
    setDataLayerTrack("Cart Viewed", {
      cartId: cart.orderNumber,
      products: cart.articles.map(convertToCartProductProperties),
    });
  }, []);

  useEffect(() => {
    if (cart.articles.length === 0) {
      onEmptyCart();
    }
  }, [cart.articles.length]);

  useEffect(() => {
    dispatch({ type: "setCartStep", step });
  }, [step, dispatch]);

  const handleUpdatePromoCode = (promoCode: string | undefined) => {
    if (configuration) {
      dispatch({
        disabled: true,
        type: "setDisabledCart",
      });

      const updatedPromoCodeJson = convertFromPromoCodeContentToApi({ promoCode });

      configuration.apiProviders.cheerz
        .updatePromoCode(updatedPromoCodeJson)
        .then((cartResponse) => {
          setPromoCodeErrorMessage(undefined);
          if (cartResponse.promo_code && cartResponse.promo_code !== "") {
            dispatchNotification({
              code: "CFV-001",
              type: "addNotificationByCode",
            });
            setDataLayerTrack("Coupon Applied", {
              couponId: cartResponse.promo_code,
              discount: cartResponse.promo_total.cents,
            });
          }
          dispatch({
            cart: {
              ...convertFromApiToCartContent(cartResponse),
            },
            type: "setSavedCart",
          });
          setDataLayerTrack(
            "Order Updated",
            convertToOrderUpdatedProperties(cart, upperFirst(step))
          );
        })
        .catch((error: ApiErrorResponse) => {
          setPromoCodeErrorMessage(error.response?.data?.error_description[0]);
          const errorDescription = error.response?.data?.error_description ?? undefined;
          if (errorDescription) {
            dispatchNotification({
              code: "CFE-001",
              message: errorDescription[0],
              type: "addNotificationByCode",
            });
          } else {
            dispatchNotification({
              code: "CFE-004",
              type: "addNotificationByCode",
            });
          }
          setDataLayerTrack("Coupon Denied", {
            couponId: promoCode,
            error: errorDescription ? errorDescription[0] : "",
          });
          dispatch({
            disabled: false,
            type: "setDisabledCart",
          });
        });
    }
  };

  return (
    <Cart
      onChangeCreditCard={onChangeCreditCard}
      onCompleteOrder={onCompleteOrder}
      onSaveCreditCard={onSaveCreditCard}
      onUpdatePromoCode={handleUpdatePromoCode}
      onValidatePostcode={onValidatePostcode}
      promoCodeErrorMessage={promoCodeErrorMessage}
      step={step}
    />
  );
};

export default CartContainer;
